import { ColDef, ICellRendererParams, ValueGetterParams } from "ag-grid-community";
import { AbstractTableColumns } from "@app/components/shared/AgGridTable/columns/AbstractTableColumns";
import {
    getLineItemAmounts,
    InvoicesListColumn,
    InvoicesListColumnTranslate,
    TInvoicesListColumnsConfig,
    TInvoiceTableItem,
    TInvoiceTablePinnedRow
} from "@inv/types";
import { formatDefault } from "@dms/scripts/helpers";
import { documentSorter } from "@dms/modules/DocumentTableModule/helpers";

import styles from "./tableColumns.module.scss";
import { CheckCircleTwoTone } from "@ant-design/icons";
import React from "react";
import { InvoicesActionList } from "../components/ActionList/InvoicesActionList";
import { ActionControlColumns } from "../components/ActionControlColumns/ActionControlColumns";
import { ContactCell } from "@inv/components/InvoicesList/components/ContactCell";
import { transformServicePeriod } from "@inv/scripts/utils/utils";

export class InvoicesListTableColumns extends AbstractTableColumns {
    static get invoiceRowNumberColumn(): Readonly<ColDef> {
        return {
            ...this.rowNumberColumn,
            field: InvoicesListColumn.INVOICE_ROW_NUMBER,
            filter: false,
            width: 70,
            pinned: "left",
            cellStyle: { fontWeight: "500", textAlign: "center" },
            valueGetter: ({ api, data, node }: ValueGetterParams) => {
                if (node.rowPinned) {
                    return null;
                }

                if (data.isChildRow) {
                    return "·";
                }

                let rowIndex = 0;
                let currentIndex = 0;

                api.forEachNodeAfterFilterAndSort(row => {
                    if (!row.data.isChildRow) {
                        currentIndex++;
                    }

                    if (row.id === node.id) {
                        rowIndex = currentIndex;
                    }
                });

                return rowIndex;
            },
        };
    }

    static get invoiceDateColumn(): Readonly<ColDef> {
        return {
            headerName: this.intl?.formatMessage({ id: InvoicesListColumnTranslate[InvoicesListColumn.INVOICE_DATE] }),
            field: InvoicesListColumn.INVOICE_DATE,
            valueGetter: ({ data }: ValueGetterParams<TInvoiceTableItem>) => {
                return data.date;
            },
            cellRenderer: ({ data, value }: ICellRendererParams<TInvoiceTableItem>) => {
                if (data.isChildRow) {
                    return null;
                }

                return value;
            },
            width: 140,
            sortable: true,
            comparator: (a, b) => documentSorter(a, b, "date"),
        };
    }

    static get invoiceNumberColumn(): Readonly<ColDef> {
        return {
            headerName: this.intl?.formatMessage({
                id: InvoicesListColumnTranslate[InvoicesListColumn.INVOICE_NUMBER],
            }),
            field: InvoicesListColumn.INVOICE_NUMBER,
            width: 220,
            valueGetter: ({ data }: ValueGetterParams<TInvoiceTableItem>) => data.invoiceNumber,
            cellRenderer: ({ data, value }: ICellRendererParams<TInvoiceTableItem>) => {
                if (data.isChildRow) {
                    return null;
                }

                return value;
            },
        };
    }
    static get invoiceStatus(): Readonly<ColDef> {
        return {
            headerName: this.intl?.formatMessage({
                id: InvoicesListColumnTranslate[InvoicesListColumn.INVOICE_STATUS],
            }),
            field: InvoicesListColumn.INVOICE_STATUS,
            width: 220,
            valueGetter: ({ data }: ValueGetterParams<TInvoiceTableItem>) => data.isConfirmed,
            cellRenderer: ({ data, value, node }: ICellRendererParams<TInvoiceTableItem>) => {
                if (data.isChildRow) {
                    return null;
                }
                if (node.rowPinned) {
                    return null;
                }

                return value ? <CheckCircleTwoTone twoToneColor={"#48b82f"} /> : "ENTWURF";
            },
        };
    }

    static get serviceDescriptionColumn(): Readonly<ColDef> {
        return {
            headerName: this.intl?.formatMessage({
                id: InvoicesListColumnTranslate[InvoicesListColumn.SERVICE_DESCRIPTION],
            }),
            headerTooltip: this.intl?.formatMessage({
                id: InvoicesListColumnTranslate[InvoicesListColumn.SERVICE_DESCRIPTION],
            }),
            field: InvoicesListColumn.SERVICE_DESCRIPTION,
            valueGetter: ({ data, node }: ValueGetterParams<TInvoiceTableItem>) => {
                if (node.rowPinned) {
                    return null;
                }
                const name = data.lineItems[0].name;
                if (data.lineItems.every(v => v.name === name)) {
                    return name;
                }

                return "Div.";
            },
            width: 200,
            sortable: true,
            comparator: (a, b) => documentSorter(a, b, "string"),
        };
    }

    static get contactColumn(): Readonly<ColDef> {
        return {
            headerName: this.intl?.formatMessage({
                id: InvoicesListColumnTranslate[InvoicesListColumn.CONTACT],
            }),
            field: InvoicesListColumn.CONTACT,
            width: 200,
            sortable: true,
            cellRenderer: ContactCell,
            comparator: (a, b) => documentSorter(a, b, "string"),
        };
    }

    static get serviceDateColumn(): Readonly<ColDef> {
        return {
            headerName: this.intl?.formatMessage({
                id: InvoicesListColumnTranslate[InvoicesListColumn.SERVICE_DATE],
            }),
            headerTooltip: this.intl?.formatMessage({
                id: InvoicesListColumnTranslate[InvoicesListColumn.SERVICE_DATE],
            }),
            field: InvoicesListColumn.SERVICE_DATE,
            width: 180,
            valueGetter: ({ data, node }: ValueGetterParams<TInvoiceTableItem>) => {
                if (node.rowPinned) {
                    return null;
                }
                const servicePeriodDaysValue = data?.servicePeriodDays
                    ? transformServicePeriod(data?.servicePeriodDays)
                    : null;
                const servicePeriodMonthsValue = data?.servicePeriodMonths
                    ? transformServicePeriod(data?.servicePeriodMonths)
                    : null;
                return data.serviceDate ?? data.deliveryDate ?? servicePeriodDaysValue ?? servicePeriodMonthsValue;
            },
            cellRenderer: ({ data, value }: ICellRendererParams<TInvoiceTableItem>) => {
                return value;
            },
        };
    }

    static get currencyColumn(): Readonly<ColDef> {
        return {
            headerName: this.intl?.formatMessage({
                id: InvoicesListColumnTranslate[InvoicesListColumn.CURRENCY],
            }),
            headerTooltip: this.intl?.formatMessage({
                id: InvoicesListColumnTranslate[InvoicesListColumn.CURRENCY],
            }),
            field: InvoicesListColumn.CURRENCY,
            width: 100,
            valueGetter: ({ data }: ValueGetterParams<TInvoiceTableItem>) => data.currencyCode,
            cellRenderer: ({ data, value }: ICellRendererParams<TInvoiceTableItem>) => {
                if (data.isChildRow) {
                    return null;
                }

                return value;
            },
        };
    }

    static get amountNettoColumn(): Readonly<ColDef> {
        return {
            headerName: this.intl?.formatMessage({
                id: InvoicesListColumnTranslate[InvoicesListColumn.AMOUNT_NETTO],
            }),
            headerTooltip: this.intl?.formatMessage({
                id: InvoicesListColumnTranslate[InvoicesListColumn.AMOUNT_NETTO],
            }),
            field: InvoicesListColumn.AMOUNT_NETTO,
            valueGetter: ({ data, node }: ValueGetterParams<TInvoiceTableItem>) => {
                if (node.rowPinned) {
                    return formatDefault((data as TInvoiceTablePinnedRow).netto);
                }
                const amount = data.lineItems.reduce((caret, item) => caret + getLineItemAmounts(item).netto, 0);

                if (!amount) {
                    return null;
                }

                return formatDefault(amount);
            },
            width: 170,
            cellStyle: { fontWeight: "500", textAlign: "right" },
        };
    }

    static get vatRateColumn(): Readonly<ColDef> {
        return {
            headerName: this.intl?.formatMessage({
                id: InvoicesListColumnTranslate[InvoicesListColumn.VAT_RATE],
            }),
            headerTooltip: this.intl?.formatMessage({
                id: InvoicesListColumnTranslate[InvoicesListColumn.VAT_RATE],
            }),
            field: InvoicesListColumn.VAT_RATE,
            valueGetter: ({ data, node }: ValueGetterParams<TInvoiceTableItem>) => {
                if (node.rowPinned) {
                    return null;
                }
                const taxRate = data.lineItems[0].tax;
                if (data.lineItems.every(v => v.tax === taxRate)) {
                    return taxRate;
                }

                return "Div.";
            },
            width: 80,
            cellStyle: { textAlign: "left" },
        };
    }

    static get vatColumn(): Readonly<ColDef> {
        return {
            headerName: this.intl?.formatMessage({
                id: InvoicesListColumnTranslate[InvoicesListColumn.VAT],
            }),
            headerTooltip: this.intl?.formatMessage({
                id: InvoicesListColumnTranslate[InvoicesListColumn.VAT],
            }),
            field: InvoicesListColumn.VAT,
            valueGetter: ({ data, node }: ValueGetterParams<TInvoiceTableItem>) => {
                if (node.rowPinned) {
                    return formatDefault((data as TInvoiceTablePinnedRow).tax);
                }
                const amount = data.lineItems.reduce((caret, item) => caret + getLineItemAmounts(item).tax, 0);

                if (!amount) {
                    return null;
                }

                return formatDefault(amount);
            },
            width: 120,
            cellStyle: { fontWeight: "500", textAlign: "right" },
        };
    }

    static get amountBruttoColumn(): Readonly<ColDef> {
        return {
            headerName: this.intl?.formatMessage({
                id: InvoicesListColumnTranslate[InvoicesListColumn.AMOUNT_BRUTTO],
            }),
            headerTooltip: this.intl?.formatMessage({
                id: InvoicesListColumnTranslate[InvoicesListColumn.AMOUNT_BRUTTO],
            }),
            field: InvoicesListColumn.AMOUNT_BRUTTO,
            valueGetter: ({ data, node }: ValueGetterParams<TInvoiceTableItem>) => {
                if (node.rowPinned) {
                    return formatDefault((data as TInvoiceTablePinnedRow).brutto);
                }

                const amount = data.lineItems.reduce((caret, item) => caret + getLineItemAmounts(item).brutto, 0);

                if (!amount) {
                    return null;
                }

                return formatDefault(amount);
            },
            width: 170,
            cellStyle: { fontWeight: "500", textAlign: "right" },
        };
    }

    private static get actionColumn(): Readonly<ColDef> {
        return {
            headerComponent: ActionControlColumns,
            field: InvoicesListColumn.ACTION,
            width: 170,
            pinned: "right",
            resizable: false,
            filter: false,
            editable: false,
            cellRenderer: InvoicesActionList,
            headerClass: styles.headerActionColumns,
            cellStyle: { textAlign: "right" },
        };
    }

    static get invoicesListInitColumnsConfig(): TInvoicesListColumnsConfig {
        return {
            [InvoicesListColumn.INVOICE_ROW_NUMBER]: true,
            [InvoicesListColumn.INVOICE_DATE]: true,
            [InvoicesListColumn.INVOICE_NUMBER]: true,
            [InvoicesListColumn.INVOICE_STATUS]: true,
            [InvoicesListColumn.SERVICE_DESCRIPTION]: true,
            [InvoicesListColumn.CONTACT]: true,
            [InvoicesListColumn.SERVICE_DATE]: true,
            [InvoicesListColumn.AMOUNT_NETTO]: true,
            [InvoicesListColumn.VAT_RATE]: true,
            [InvoicesListColumn.VAT]: true,
            [InvoicesListColumn.AMOUNT_BRUTTO]: true,
            [InvoicesListColumn.CURRENCY]: true,
            [InvoicesListColumn.ACTION]: true,
        };
    }

    static get requiredColumnsConfig(): string[] {
        return [InvoicesListColumn.INVOICE_ROW_NUMBER, InvoicesListColumn.INVOICE_DATE, InvoicesListColumn.ACTION];
    }

    private static getColumn = (colKey: InvoicesListColumn): Readonly<ColDef> => {
        switch (colKey) {
            case InvoicesListColumn.INVOICE_ROW_NUMBER:
                return { ...this.invoiceRowNumberColumn };
            case InvoicesListColumn.INVOICE_DATE:
                return { ...this.invoiceDateColumn };
            case InvoicesListColumn.INVOICE_NUMBER:
                return { ...this.invoiceNumberColumn };
            case InvoicesListColumn.INVOICE_STATUS:
                return { ...this.invoiceStatus };
            case InvoicesListColumn.SERVICE_DESCRIPTION:
                return { ...this.serviceDescriptionColumn };
            case InvoicesListColumn.CONTACT:
                return { ...this.contactColumn };
            case InvoicesListColumn.SERVICE_DATE:
                return { ...this.serviceDateColumn };
            case InvoicesListColumn.CURRENCY:
                return { ...this.currencyColumn };
            case InvoicesListColumn.AMOUNT_NETTO:
                return { ...this.amountNettoColumn };
            case InvoicesListColumn.VAT_RATE:
                return { ...this.vatRateColumn };
            case InvoicesListColumn.VAT:
                return { ...this.vatColumn };
            case InvoicesListColumn.AMOUNT_BRUTTO:
                return { ...this.amountBruttoColumn };
            case InvoicesListColumn.ACTION:
                return { ...this.actionColumn };
        }
    };

    public static getColumns = (activeKeys?: string[]): ColDef[] =>
        activeKeys.map(item => this.getColumn(item as InvoicesListColumn));
}
